import {useEffect, useMemo, useState} from 'react';
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Divider,
    Stack,
} from '@mui/material';
import {TaxHeader} from '@/pages/components/tax-computation/sub';
import {MoneyField} from '@/components/common';
import {useCompanyAnnualStore, useTaxStore} from '@/store/audit';
import type {TaxWithholdingScheduleVO} from '@/services/auditService/interface/tax-com';

interface TaxSubP11aProps {
    annualId: number;
    p11aData?: TaxWithholdingScheduleVO | null;
}

function formatRatePct(rate?: number | null): string {
    if (rate == null || !Number.isFinite(Number(rate))) {
        return '';
    }
    const pct = Number(rate) * 100;
    return `${parseFloat(pct.toFixed(4))}%`;
}

function formatMoneyParen(n: number): string {
    const abs = Math.abs(n);
    const formatted = abs.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
    return n < 0 ? `(${formatted})` : formatted;
}

export function TaxSubP11a({annualId, p11aData: p11aDataProp}: TaxSubP11aProps) {
    const getTaxComputationInfo = useCompanyAnnualStore((state) => state.getTaxComputationInfo);
    const getCompanyDetailsInfo = useCompanyAnnualStore((state) => state.getCompanyDetailsInfo);

    const initialize = useTaxStore((state) => state.initialize);
    const storeP11aData = useTaxStore((state) => state.p11aData);
    const p11aData = p11aDataProp !== undefined ? p11aDataProp : storeP11aData;

    const [loading, setLoading] = useState(true);
    const [taxInfo, setTaxInfo] = useState<{
        companyName: string;
        yearOfAssessment: string | null;
        presentationCurrency: string | null;
        taxFileNo: string | null;
    } | null>(null);

    useEffect(() => {
        const loadHeader = async () => {
            setLoading(true);
            try {
                const info = await getTaxComputationInfo(annualId);
                setTaxInfo(info);

                const companyDetails = await getCompanyDetailsInfo(annualId);
                const companyId = companyDetails?.companyId;

                if (companyId) {
                    await initialize(companyId, annualId);
                } else {
                    console.error('Company ID not found');
                }
            } catch (error) {
                console.error('Failed to load tax computation data:', error);
            } finally {
                setLoading(false);
            }
        };

        loadHeader();
    }, [annualId, getTaxComputationInfo, getCompanyDetailsInfo, initialize]);

    const currency = taxInfo?.presentationCurrency || 'HK$';
    const rows = p11aData?.rows || [];
    const credit = p11aData?.unilateralCredit;

    const totalWht = useMemo(() => {
        if (p11aData?.totalWithholdingTax != null) {
            return Number(p11aData.totalWithholdingTax) || 0;
        }
        return rows.reduce((s, r) => s + (Number(r?.withholdingTaxAmount) || 0), 0);
    }, [p11aData?.totalWithholdingTax, rows]);

    if (loading) {
        return (
            <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!taxInfo) {
        return <Box sx={{p: 2}}>Failed to load data</Box>;
    }

    return (
        <Box sx={{p: 3}}>
            <TaxHeader
                companyName={taxInfo.companyName}
                fileNo={taxInfo.taxFileNo || ''}
                yearOfAssessment={taxInfo.yearOfAssessment}
            />

            <Typography
                variant="subtitle1"
                sx={{fontWeight: 700, textAlign: 'center', textDecoration: 'underline', mb: 2}}
            >
                BREAKDOWN OF WITHHOLDING TAX
            </Typography>

            <TableContainer component={Paper} sx={{mb: 2}}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">Income Nature</TableCell>
                            <TableCell align="center">Taxable Jurisdiction</TableCell>
                            <TableCell align="center">Income Amount</TableCell>
                            <TableCell align="center">Foreign Tax Rate</TableCell>
                            <TableCell align="center">Withholding Tax Amount</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={2}/>
                            <TableCell align="center" sx={{textDecoration: 'underline'}}>{currency}</TableCell>
                            <TableCell/>
                            <TableCell align="center" sx={{textDecoration: 'underline'}}>{currency}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={`${row.incomeNature || ''}-${idx}`}>
                                <TableCell>{row.incomeNature || ''}</TableCell>
                                <TableCell>{row.jurisdiction || ''}</TableCell>
                                <TableCell align="right">
                                    <MoneyField
                                        value={Number(row.incomeAmount) || 0}
                                        editable={false}
                                        showCents
                                        align="right"
                                        compact
                                        currencySymbol={currency}
                                    />
                                </TableCell>
                                <TableCell align="right">{formatRatePct(row.foreignTaxRate)}</TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                        <MoneyField
                                            value={Number(row.withholdingTaxAmount) || 0}
                                            editable={false}
                                            showCents
                                            align="right"
                                            compact
                                            currencySymbol={currency}
                                        />
                                        {row.unilateralEligible ? (
                                            <Typography variant="caption" color="text.secondary">(*)</Typography>
                                        ) : null}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        ))}
                        <TableRow>
                            <TableCell colSpan={4} align="right" sx={{fontWeight: 700}}>
                                Total Withholding Tax Amount
                            </TableCell>
                            <TableCell align="right" sx={{fontWeight: 700}}>
                                <MoneyField
                                    value={totalWht}
                                    editable={false}
                                    showCents
                                    align="right"
                                    compact
                                    currencySymbol={currency}
                                />
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="body2" color="text.secondary" sx={{mb: 2}}>
                Note (*): Unilateral Tax Credit under Section 50 of the Inland Revenue Ordinance
            </Typography>

            {credit ? (
                <Box sx={{mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1}}>
                    <Typography variant="subtitle2" sx={{fontWeight: 700, mb: 1}}>
                        UNILATERAL TAX CREDIT
                    </Typography>
                    <Stack spacing={0.75}>
                        <Typography variant="body2">
                            Profits tax on eligible income (a) = {formatMoneyParen(Number(credit.eligibleIncomeSum) || 0)}
                            {' × '}{formatRatePct(credit.rate)}
                            {' = '}<b>{formatMoneyParen(Number(credit.profitsTaxOnEligibleIncome) || 0)}</b>
                        </Typography>
                        <Typography variant="body2">
                            Withholding tax paid (b) = <b>{formatMoneyParen(Number(credit.withholdingTaxPaid) || 0)}</b>
                        </Typography>
                        <Typography variant="body2">
                            Unilateral Tax Credit Limit, minimum(a,b) ={' '}
                            <b>{formatMoneyParen(Number(credit.creditLimit) || 0)}</b>
                        </Typography>
                        <Divider sx={{my: 1}}/>
                        <Typography variant="body2">
                            Note (**): Excess withholding tax not eligible for tax credit, but deductible as an expense
                            (@ a−b) = <b>{formatMoneyParen(Number(credit.excessWithholdingTax) || 0)}</b>
                        </Typography>
                    </Stack>
                </Box>
            ) : null}
        </Box>
    );
}
